/**
 * A2's hero — an in-house illustration where the frame draws a photograph placeholder.
 *
 * The frame's box is a dashed placeholder asking for "real photography" of "one person,
 * one need", and until W2-12 the product shipped the placeholder itself, its two lines of
 * instruction to a designer shown to every new customer as if they were the welcome.
 * There is no photograph and no budget for one (HANDOFF § 0, rule 7), so the box carries
 * a drawing instead: a phone at the centre with a question asked and an answer given, and
 * around it the five needs the tagline beneath names in order — বিল, খাবার, বাজার, রাইড,
 * টেকনিশিয়ান — each a glyph in a ring, each joined to the phone.
 *
 * Drawn only from the palette (`@oprappo/tokens § color`), strokes at the § 01 icon
 * weight of 2, no gradient (docs/01 § prohibited) and exactly one amber element — the
 * answer — because amber is one element per screen and A2's button is teal. Decorative:
 * `aria-hidden`, since every word it stands for is in the tagline under it. It scales to
 * the box and keeps its proportions at every phone width.
 *
 * Recorded as a departure from the frame in `docs/deviations/W2-12.md`, A2.
 */
import { color } from '@oprappo/tokens';

const STROKE = { fill: 'none', stroke: color.teal, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** One need: a white ring with its glyph, at (x, y) in the 312×300 drawing. */
function Need({ x, y, children }: { x: number; y: number; children: JSX.Element }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={22} fill="#ffffff" stroke={color.borderCool} strokeWidth={2} />
      {children}
    </g>
  );
}

export function WelcomeHero(): JSX.Element {
  return (
    <svg
      data-a2-hero=""
      viewBox="0 0 312 300"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className="w-full h-full"
    >
      {/* The ground: one ring of sand on the recessed box, the phone's whole world. */}
      <circle cx={156} cy={150} r={118} fill={color.sand} stroke={color.border} strokeWidth={2} />

      {/* Five threads from the needs to the phone. */}
      <g stroke={color.borderWarm} strokeWidth={2} strokeDasharray="3 6" strokeLinecap="round" fill="none">
        <path d="M78 86 L114 108" />
        <path d="M234 86 L198 108" />
        <path d="M62 182 L114 170" />
        <path d="M250 182 L198 170" />
        <path d="M156 248 L156 226" />
      </g>

      {/* The phone. */}
      <rect x={114} y={58} width={84} height={166} rx={14} fill="#ffffff" stroke={color.teal} strokeWidth={3} />
      <path d="M144 70 H168" {...STROKE} stroke={color.border} strokeWidth={3} />
      {/* A question, in the teal of a person's own words… */}
      <rect x={124} y={88} width={56} height={24} rx={8} fill={color.teal} />
      <path d="M132 112 L132 120 L140 112 Z" fill={color.teal} />
      <path d="M133 97 H165 M133 104 H155" stroke={color.onTealBody} strokeWidth={2} strokeLinecap="round" />
      {/* …and the answer, the one amber thing on the screen. */}
      <rect x={140} y={128} width={48} height={24} rx={8} fill={color.amber} />
      <path d="M180 152 L180 160 L172 152 Z" fill={color.amber} />
      <path d="M149 137 H179 M149 144 H169" stroke={color.amberInk} strokeWidth={2} strokeLinecap="round" />
      {/* Rows waiting under it. */}
      <rect x={124} y={170} width={64} height={7} rx={3.5} fill={color.sandDeep} />
      <rect x={124} y={184} width={46} height={7} rx={3.5} fill={color.sandDeep} />
      <path d="M144 212 H168" stroke={color.border} strokeWidth={3} strokeLinecap="round" />

      {/* বিল — a bill with its lines. */}
      <Need x={64} y={78}>
        <g {...STROKE}>
          <path d="M-7 -10 H4 L8 -6 V10 H-7 Z" />
          <path d="M-3 -3 H4 M-3 2 H4 M-3 7 H1" />
        </g>
      </Need>
      {/* খাবার — a bowl, steaming. */}
      <Need x={248} y={78}>
        <g {...STROKE}>
          <path d="M-10 0 H10 A10 9 0 0 1 -10 0 Z" />
          <path d="M-4 -5 Q-6 -8 -4 -11 M3 -5 Q1 -8 3 -11" />
        </g>
      </Need>
      {/* বাজার — a bag with handles. */}
      <Need x={48} y={186}>
        <g {...STROKE}>
          <path d="M-9 -4 H9 L7 10 H-7 Z" />
          <path d="M-4 -4 V-7 A4 4 0 0 1 4 -7 V-4" />
        </g>
      </Need>
      {/* রাইড — a car, side on. */}
      <Need x={264} y={186}>
        <g {...STROKE}>
          <path d="M-11 4 V-1 L-7 -7 H7 L11 -1 V4 Z" />
          <circle cx={-6} cy={6} r={2.5} />
          <circle cx={6} cy={6} r={2.5} />
        </g>
      </Need>
      {/* টেকনিশিয়ান — a spanner: a handle, and a ring with its jaw cut open. */}
      <Need x={156} y={262}>
        <g {...STROKE}>
          <path d="M-9 9 L1.5 -1.5" />
          <circle cx={5} cy={-5} r={5} />
          <path d="M6.5 -11.5 L11.5 -6.5" stroke="#ffffff" strokeWidth={4} />
        </g>
      </Need>
    </svg>
  );
}
